type ManagedBodyChildState = {
	ariaHidden: string | null;
	inert: boolean;
};

type InertHTMLElement = HTMLElement & { inert?: boolean };

const GWP_OVERLAY_ROOT_ID = 'gwp-overlay-root';
const managedBodyChildren = new Map<HTMLElement, ManagedBodyChildState>();
const tabbableSelector = [
	'a[href]',
	'button:not([disabled])',
	'textarea:not([disabled])',
	'input:not([disabled])',
	'select:not([disabled])',
	'[tabindex]:not([tabindex="-1"])',
].join(',');
const overlayCloseSelector = '[data-gwp-overlay-close="true"]';

let activeOverlayCount = 0;
let overlayPortalRoot: HTMLDivElement | null = null;

function snapshotBodyChildState(element: HTMLElement) {
	if (managedBodyChildren.has(element)) {
		return;
	}

	managedBodyChildren.set(element, {
		ariaHidden: element.getAttribute('aria-hidden'),
		inert: (element as InertHTMLElement).inert === true,
	});
}

function applyBodyChildVisibility(element: HTMLElement, hidden: boolean) {
	snapshotBodyChildState(element);

	if (hidden) {
		element.setAttribute('aria-hidden', 'true');
		(element as InertHTMLElement).inert = true;
		return;
	}

	const storedState = managedBodyChildren.get(element);
	if (!storedState) {
		return;
	}

	if (storedState.ariaHidden === null) {
		element.removeAttribute('aria-hidden');
	} else {
		element.setAttribute('aria-hidden', storedState.ariaHidden);
	}

	(element as InertHTMLElement).inert = storedState.inert;
}

export function getGWPOverlayRoot() {
	if (typeof document === 'undefined') {
		return null;
	}

	if (overlayPortalRoot !== null && overlayPortalRoot.isConnected) {
		return overlayPortalRoot;
	}

	const existingRoot = document.getElementById(GWP_OVERLAY_ROOT_ID);
	if (existingRoot instanceof HTMLDivElement) {
		overlayPortalRoot = existingRoot;
		return overlayPortalRoot;
	}

	const nextRoot = document.createElement('div');
	nextRoot.id = GWP_OVERLAY_ROOT_ID;
	document.body.appendChild(nextRoot);
	overlayPortalRoot = nextRoot;
	return overlayPortalRoot;
}

function syncOverlayEnvironment() {
	if (typeof document === 'undefined') {
		return;
	}

	const portalRoot = getGWPOverlayRoot();
	const bodyChildren = Array.from(document.body.children).filter((element): element is HTMLElement => element instanceof HTMLElement);

	if (portalRoot === null || activeOverlayCount === 0) {
		managedBodyChildren.forEach((_state, element) => {
			applyBodyChildVisibility(element, false);
		});
		managedBodyChildren.clear();
		return;
	}

	bodyChildren.forEach((element) => {
		applyBodyChildVisibility(element, element !== portalRoot);
	});
}

export function registerGWPOverlay() {
	activeOverlayCount += 1;
	syncOverlayEnvironment();
}

export function unregisterGWPOverlay() {
	activeOverlayCount = Math.max(0, activeOverlayCount - 1);
	syncOverlayEnvironment();
}

export function getGWPFocusableElements(container: HTMLElement | null): HTMLElement[] {
	if (container === null) {
		return [];
	}

	return Array.from(container.querySelectorAll<HTMLElement>(tabbableSelector)).filter((element) => {
		if (element.hasAttribute('disabled')) {
			return false;
		}

		if (element.getAttribute('aria-hidden') === 'true') {
			return false;
		}

		return element.offsetParent !== null || element === document.activeElement;
	});
}

export function getGWPInitialFocusTarget(
	container: HTMLElement | null,
	preferredTarget?: HTMLElement | null,
	fallbackTarget?: HTMLElement | null
) {
	const focusableElements = getGWPFocusableElements(container);

	if (preferredTarget !== undefined && preferredTarget !== null && focusableElements.includes(preferredTarget)) {
		return preferredTarget;
	}

	const nonCloseFocusableElements = focusableElements.filter((element) => !element.matches(overlayCloseSelector));
	if (nonCloseFocusableElements.length > 0) {
		return nonCloseFocusableElements[0];
	}

	if (fallbackTarget !== undefined && fallbackTarget !== null) {
		return fallbackTarget;
	}

	return focusableElements[0] ?? null;
}
